import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import { fetchRepairRequestCorrections } from '#/api/aftersalesMgmt/repairRequest/corrections';
import { cn } from '#/lib/utils';
import { getRepairRequestApiErrorMessage } from '#/modules/crmMgmt/aftersales/repairRequest/helper/apiError';
import { summarizeCorrectionEntry } from '#/modules/crmMgmt/aftersales/repairRequest/helper/correctionHistory';
import {
  repairRequestCorrectionsQueryKey,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/correctionQueryKeys';
import { formatDateTimeOrDash } from '#/utils/date';

/** 內部更正紀錄（客戶不可見），依紀錄順序新到舊。詳情頁分頁與側欄 dialog 共用。 */
const RepairRequestCorrectionHistoryList = ({ detail, enabled = true, className }) => {
  const id = detail?.id;
  const query = useQuery({
    queryKey: repairRequestCorrectionsQueryKey(id),
    queryFn: () => fetchRepairRequestCorrections(id),
    enabled: enabled && Boolean(id),
  });

  const serialByLineId = useMemo(() => Object.fromEntries(
    (detail?.lines ?? []).map((line) => [line.id, line.serialNo]),
  ), [detail?.lines]);

  const entries = useMemo(
    () => [...(query.data?.data ?? [])].reverse(),
    [query.data],
  );

  return (
    <div className={cn('space-y-4', className)}>
      {query.isLoading ? <p className="text-sm text-slate-500">載入中…</p> : null}
      {query.isError ? (
        <p className="text-sm text-destructive">
          {getRepairRequestApiErrorMessage(query.error, '載入更正紀錄失敗')}
        </p>
      ) : null}
      {query.isSuccess && entries.length === 0 ? (
        <p className="text-sm text-slate-500">尚無更正紀錄</p>
      ) : null}
      {entries.map((entry) => {
        const { kind, rows } = summarizeCorrectionEntry(entry, serialByLineId);
        return (
          <section key={entry.id} className="rounded-md border border-slate-200">
            <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 bg-slate-50 px-3 py-2">
              <span className="text-sm font-semibold text-gray-800">{kind}</span>
              <span className="text-xs text-gray-500 tabular-nums">
                {formatDateTimeOrDash(entry.changedAt)}
              </span>
              <span className="text-sm text-gray-700">{`原因：${entry.reason || '—'}`}</span>
            </header>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="w-1/4 px-3 py-1.5 font-medium">欄位</th>
                  <th className="px-3 py-1.5 font-medium">更正前</th>
                  <th className="px-3 py-1.5 font-medium">更正後</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-1.5 text-gray-600">{row.label}</td>
                    <td className="px-3 py-1.5 whitespace-pre-line text-gray-500">{row.before}</td>
                    <td className="px-3 py-1.5 whitespace-pre-line text-gray-800">{row.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
};

RepairRequestCorrectionHistoryList.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  enabled: PropTypes.bool,
  className: PropTypes.string,
};

export default RepairRequestCorrectionHistoryList;
